/// <amd-module name="Release/Commands/PipelineCommandsService" />
import * as React from "react";

import { Services, VssService, IVssService, IVssPageContext } from "VSS/Platform/Context";
import { IVssLayoutManager, WrappedComponent, IWrappedComponentProperties } from "VSS/Platform/Layout";
import { ObservableValue } from "VSS/Core/Observable";
import { localeFormat } from "VSS/Core/Util/String";

import { IMenuItem } from "VSSUI/Menu";
import { IIconProps } from "VSSUI/Icon";
import { Checkbox } from "VSSUI/Checkbox";

import { getTFSPageData, IProjectData } from "Tfs/Platform/Page";

import { Favorite } from "Favorites/Common/Contracts/Favorite";
import { IFavoritesService, IFavoritesCollection } from "Favorites/Common/FavoritesService";

import { IReleaseDefinitionPivotCommandContext } from "Release/Client/ExtensionContracts/IMenuCommandContracts";
import { IReleaseService } from "Release/Client/Service/Release";
import { ReleaseDefinition } from "Release/Client/Contracts/Release";

import { createUrlForRoute, handleEventAndNavigate } from "Release/Helpers/Link";
import { hasRmPermissions, ReleaseManagementSecurityPermissions, userHasMemberClaim, hasRmUIPermissions, ReleaseManagementUISecurityPermissions } from "Release/Helpers/Permission";
import { publishClickEvent, publishSuccessEvent, publishFailedEvent } from "Release/Helpers/Telemetry";
import { IPipelineDialogWithCommentProps, IPipelineDialogPropsBase, IMovePipelineDialogProps, IImportPipelineDialogProps, IRenamePipelineDialogProps } from "DialogTypes"
import * as Resources from "Release/Commands/Resources";

export const newItemParentAction: (context: IReleaseDefinitionPivotCommandContext, parentButtonId: string) => IMenuItem | undefined = (context: IReleaseDefinitionPivotCommandContext, parentButtonId: string) => {
    if (context.folderPath ?
        hasRmPermissions(context.pageContext, context.folderPath, context.pipelineId, ReleaseManagementSecurityPermissions.EditReleaseDefinition)
        : userHasMemberClaim(context.pageContext)) {
        return _getPivotBarAction(
            context,
            parentButtonId,
            Resources.NewPipelineParentCommand,
            { iconName: "Add" },
            undefined,
            true,
            [
                _getPivotBarAction(context, "create-release-pipeline-command", Resources.NewPipelineCommand, { iconName: "Add" }, "createNewPipeline", undefined, undefined, _getCreatePipelineUrl(context.pageContext, context)),
                _getPivotBarAction(context, "import-release-pipeline-command", Resources.ImportPipelineCommand, { iconName: "Upload" }, "importPipeline"),
            ]);
    }
};

export const clonePipelineCommand: (context: IReleaseDefinitionPivotCommandContext) => IMenuItem | undefined = (context: IReleaseDefinitionPivotCommandContext) => {
    if (userHasMemberClaim(context.pageContext)) {
        return _getPivotBarAction(context, "clone-release-pipeline-command", Resources.ClonePipeline, { iconName: "Copy" }, "clonePipeline", undefined, undefined, _getClonePipelineUrl(context.pageContext, context));
    }
};

export const editPipelineCommand: (context: IReleaseDefinitionPivotCommandContext) => IMenuItem | undefined = (context: IReleaseDefinitionPivotCommandContext) => {
    const editCommand = _getPivotBarAction(context, "edit-release-pipeline-command", Resources.EditPipeline, { iconName: "Edit" }, "editPipeline", undefined, undefined, _getEditPipelineUrl(context.pageContext, context));

    if (hasRmPermissions(context.pageContext, context.folderPath, context.pipelineId, ReleaseManagementSecurityPermissions.EditReleaseDefinition)) {
        return editCommand;
    }
    else if (userHasMemberClaim(context.pageContext)) {
        editCommand.text = Resources.ViewPipeline;
        editCommand.iconProps = { iconName: "EntryView" };

        return editCommand;
    }
};

export const deletePipelineCommand: (context: IReleaseDefinitionPivotCommandContext) => IMenuItem | undefined = (context: IReleaseDefinitionPivotCommandContext) => {
    if (hasRmPermissions(context.pageContext, context.folderPath, context.pipelineId, ReleaseManagementSecurityPermissions.DeleteReleaseDefinition)) {
        return _getPivotBarAction(context, "delete-release-pipeline-command", Resources.DeletePipeline, { iconName: "Delete" }, "deletePipeline");
    }
};

export const addRemoveFavoritesCommand: (context: IReleaseDefinitionPivotCommandContext) => Promise<IMenuItem | undefined> = (context: IReleaseDefinitionPivotCommandContext) => {
    const favoritesService: IFavoritesService = context.pageContext.getService("IFavoritesService");
    if (!favoritesService.canUseFavorites() || !context.pipelineId) {
        return Promise.resolve(undefined);
    }

    return isFavorite(context.pageContext, context.pipelineId)
        .then((isPiplineFavorited) => {
            return _getPivotBarAction(
                context,
                "add-remove-release-pipeline-to-favorites",
                isPiplineFavorited ? Resources.RemoveFromFavorites : Resources.AddToFavorites,
                {
                   
                    iconName: isPiplineFavorited ? "FavoriteStar" : "FavoriteStarFill"
                },
                isPiplineFavorited ? "removeFromFavorites" : "addToFavorites");
        });
};

export const movePipelineCommand: (context: IReleaseDefinitionPivotCommandContext, movePipelineCallback: (newParentPath: string) => any) => IMenuItem | undefined = (context: IReleaseDefinitionPivotCommandContext, movePipelineCallback: (newParentPath: string) => any) => {
    if (hasRmPermissions(context.pageContext, context.folderPath, context.pipelineId, ReleaseManagementSecurityPermissions.EditReleaseDefinition)) {
        const moveCommand = _getPivotBarAction(context, "move-pipeline-command", Resources.MovePipelineCommand, { iconName: "Folder" });
        moveCommand.onActivate = (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            context.pageContext.getService<IPipelineCommandsService>(pipelineCommandServiceName).movePipeline(context, movePipelineCallback);
        };

        return moveCommand;
    }
};

export const renamePipelineCommand: (context: IReleaseDefinitionPivotCommandContext, pipelineRenameCallback: (newPipelineName: string) => any) => IMenuItem | undefined = (context: IReleaseDefinitionPivotCommandContext, pipelineRenameCallback: (newPipelineName: string) => any) => {
    if (hasRmPermissions(context.pageContext, context.folderPath, context.pipelineId, ReleaseManagementSecurityPermissions.EditReleaseDefinition)) {
        const renameCommand = _getPivotBarAction(context, "rename-pipeline-command", Resources.RenamePipelineCommand, { iconName: "Rename" });
        renameCommand.onActivate = (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            context.pageContext.getService<IPipelineCommandsService>(pipelineCommandServiceName).renamePipeline(context, pipelineRenameCallback);
        }

        return renameCommand;
    }
};

export const exportPipelineCommand: (context: IReleaseDefinitionPivotCommandContext) => IMenuItem | undefined = (context: IReleaseDefinitionPivotCommandContext) => {
    if (!hasRmUIPermissions(context.pageContext, ReleaseManagementUISecurityPermissions.ExportReleaseDefinition)) {
        return;
    }

    return _getPivotBarAction(context, "export-release-pipeline-command", Resources.ExportPipeline, { iconName: "Download" }, "exportPipeline");
};

/**
 * File result from files uploaded to the FileInput.
 */
interface FileInputResult {
    /**
     * The name of the file.
     */
    name: string;
    /**
     * The file type.
     */
    type: string;
    /**
     * The size of the file, in bytes.
     */
    size: number;
    /**
     * The last-modified timestamp.
     */
    lastModifiedDate: Date;
    /**
     * For Base64EncodedText or RawText, the content of the file.
     */
    content?: string;
    /**
     * For RawFile, the actual file.
     */
    file?: File;
    /**
     * A friendly status description, e.g. "2MB"
     */
    statusText: string;
    /**
     * Indicates whether there was a problem loading the file.
     */
    isError: boolean;
}

export interface IPipelineCommandsService extends IVssService {
    createNewPipeline(context: IReleaseDefinitionPivotCommandContext, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void;
    importPipeline(context: IReleaseDefinitionPivotCommandContext, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void;
    editPipeline(context: IReleaseDefinitionPivotCommandContext, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void;
    clonePipeline(context: IReleaseDefinitionPivotCommandContext, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void;
    deletePipeline(context: IReleaseDefinitionPivotCommandContext): void;
    addToFavorites(context: IReleaseDefinitionPivotCommandContext): void;
    removeFromFavorites(context: IReleaseDefinitionPivotCommandContext): void;
    movePipeline(context: IReleaseDefinitionPivotCommandContext, movePipelineCallback: any): void;
    renamePipeline(context: IReleaseDefinitionPivotCommandContext, pipelineRenameCallback: any): void;
    exportPipeline(context: IReleaseDefinitionPivotCommandContext): void;
}

class PipelineCommandsService extends VssService implements IPipelineCommandsService {
    public createNewPipeline(context: IReleaseDefinitionPivotCommandContext, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void {
        publishClickEvent(this.pageContext, newPipelineFeature, context.telemetrySource);
        const editorUrl = _getCreatePipelineUrl(this.pageContext, context);
        handleEventAndNavigate(this.pageContext, event, editorUrl);
    }

    public importPipeline(context: IReleaseDefinitionPivotCommandContext, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void {
        publishClickEvent(this.pageContext, importPipelineFeature, context.telemetrySource);
        _renderWrappedComponent<IImportPipelineDialogProps>(this.pageContext, "rd-import-dilaog", {
            onOkClick: (file: FileInputResult | null) => {
                const storageKey = "microsoft.vsts.releasemanagement.importedDefinition";
                window.sessionStorage.setItem(storageKey, (file as FileInputResult).content || "");
                setTimeout(() => { window.sessionStorage.removeItem(storageKey); }, 3 * 60 * 1000 /* 3 mins*/);

                const queryParams: { [key: string]: string } = {
                    "_a": "action-import-definition",
                    "path": context.folderPath || "\\",
                    "source": "ReleaseImport"
                };

                const editorUrl = createUrlForRoute(this.pageContext, "ms.vss-releaseManagement-web.cd-workflow-hub-default-route", queryParams);
                handleEventAndNavigate(this.pageContext, event, editorUrl);
            }
        });
    }

    public editPipeline(context: IReleaseDefinitionPivotCommandContext, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void {
        publishClickEvent(this.pageContext, editRdFeature, context.telemetrySource);
        if (context.pipelineId) {
            const editorUrl = _getEditPipelineUrl(this.pageContext, context);
            handleEventAndNavigate(this.pageContext, event, editorUrl!);
        }
    }

    public clonePipeline(context: IReleaseDefinitionPivotCommandContext, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void {
        publishClickEvent(this.pageContext, cloneRdFeature, context.telemetrySource);
        if (context.pipelineId) {
            const editorUrl = _getClonePipelineUrl(this.pageContext, context);
            handleEventAndNavigate(this.pageContext, event, editorUrl!);
        }
    }

    public deletePipeline(context: IReleaseDefinitionPivotCommandContext): void {
        publishClickEvent(this.pageContext, deleteDefinitionFeature, context.telemetrySource);
        if (context.pipelineId) {

            _renderWrappedComponent<IPipelineDialogWithCommentProps<void>>(
                this.pageContext,
                "rel-pipe-comment-dialog",
                {
                    commentBoxTitle: Resources.Comment,
                    dialogTitle: localeFormat(Resources.DeleteDialogTitle, context.pipelineName),
                    onOkClick: (comment: string) => {
                        if (context.pipelineId) {
                            return this.pageContext.getService<IReleaseService>("IReleaseService")
                                .deleteReleaseDefinition(context.pipelineId, comment, this._forceDelete.value)
                                .then(() => {
                                    publishSuccessEvent(this.pageContext, deleteDefinitionFeature, context.telemetrySource);
                                    context.successCallback(localeFormat(Resources.PipelineDeletedMessage, context.pipelineName), true);
                                }, (error) => {
                                    publishFailedEvent(this.pageContext, deleteDefinitionFeature, context.telemetrySource);
                                    if (context.errorCallback) {
                                        const errorMessage: string = error ? error.message || error : "";
                                        context.errorCallback(errorMessage);
                                    }
                                });
                        }
                    },
                    onClose: () => this._forceDelete.value = false /* Reset forceDelete when the dialog closes*/
                },
                [this._getDeleteDialogContent(context.pipelineName || "")]);

        }
    }

    public addToFavorites(context: IReleaseDefinitionPivotCommandContext): void {
        publishClickEvent(context.pageContext, addToFavoritesFeature, context.telemetrySource);

        const project = _getProject(context.pageContext);
        const projectName = (project && project.name) || "";
        const favorite = {
            artifactId: context.pipelineId ? context.pipelineId.toString() : "",
            artifactName: context.pipelineName,
            artifactType: releasePipelineArtifactType,
            artifactScope: {
                id: (project && project.id) || "",
                name: projectName,
                type: artifactScopeType
            }
        } as Favorite;

        context.pageContext.getService<IFavoritesService>("IFavoritesService")
            .addFavorite(favorite)
            .then(() => {
                publishSuccessEvent(context.pageContext, addToFavoritesFeature, context.telemetrySource);
            }, (error) => {
                publishFailedEvent(context.pageContext, addToFavoritesFeature, context.telemetrySource);
                this._handleError(context, error);
            });
    }

    public removeFromFavorites(context: IReleaseDefinitionPivotCommandContext): void {
        publishClickEvent(context.pageContext, removeFromFavoritesFeature, context.telemetrySource);

        const favoritesService = context.pageContext.getService<IFavoritesService>("IFavoritesService");
        const project = _getProject(context.pageContext);
        const projectId = (project && project.id) || "";

        favoritesService.getFavorites(
            releasePipelineArtifactType,
            artifactScopeType,
            projectId
        ).then((favorites: IFavoritesCollection) => {
            favorites.value.forEach((value: Favorite) => {
                if (parseInt(value.artifactId) === context.pipelineId) {
                    favoritesService.removeFavorite(value).then(() => {
                        publishSuccessEvent(context.pageContext, removeFromFavoritesFeature, context.telemetrySource);
                    }, (error) => {
                        publishFailedEvent(context.pageContext, removeFromFavoritesFeature, context.telemetrySource);
                        this._handleError(context, error);
                    });
                }
            });
        }, () => {
            publishFailedEvent(context.pageContext, removeFromFavoritesFeature, context.telemetrySource);
        });
    }

    public movePipeline(context: IReleaseDefinitionPivotCommandContext, movePipelineCallback: any): void {
        _renderWrappedComponent<IMovePipelineDialogProps>(context.pageContext, "move-rd-dialog",
            {
                initialPath: context.folderPath,
                name: context.pipelineName as string,
                onPipelineMoved: (newParentPath: string) => {
                    movePipelineCallback(newParentPath);
                    // context.successCallback(`Folder created with path: ${newFolderPath}`);
                }
            });
    }

    public renamePipeline(context: IReleaseDefinitionPivotCommandContext, pipelineRenameCallback: any): void {
        _renderWrappedComponent<IRenamePipelineDialogProps>(context.pageContext, "rename-rd-dialog",
            {
                parentPath: context.folderPath,
                initialName: context.pipelineName as string,
                onPipelineRenamed: (newPipelineName: string) => {
                    pipelineRenameCallback(newPipelineName);
                    // context.successCallback(`Folder created with path: ${newFolderPath}`);
                }
            });
    }

    public exportPipeline(context: IReleaseDefinitionPivotCommandContext): void {
        publishClickEvent(this.pageContext, exportRdFeature, context.telemetrySource);
        if (context.pipelineId) {
            this.pageContext.getService<IReleaseService>("IReleaseService")
                .getReleaseDefinition(context.pipelineId)
                .then((definition: ReleaseDefinition) => {
                    publishSuccessEvent(this.pageContext, exportRdFeature, context.telemetrySource);
                    const definitionJson = JSON.stringify(definition);
                    const definitionName = definition.name;
                    const exportedFileName = definitionName.concat(".json");
                    saveToFile(definitionJson, exportedFileName);
                },
                    (error) => {
                        // TODO : How do we handle errors for contributed menu items??
                        publishFailedEvent(this.pageContext, exportRdFeature, context.telemetrySource);
                    });
        }
    }

    private _handleError(context: IReleaseDefinitionPivotCommandContext, error: any): void {
        if (context.errorCallback) {
            const errorMessage: string = error ? error.message || error : "";
            context.errorCallback(errorMessage);
        }
    }

    private _getDeleteDialogContent(pipelineName: string): React.ReactNode {
        return React.createElement(
            "div",
            { className: "delete-dialog-content" },
            React.createElement("div", { style: { marginBottom: 15 } }, localeFormat(Resources.DeleteDefinitionConfirmationMessage, pipelineName)),
            React.createElement(Checkbox, {
                onChange: (ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, checked: boolean) => this._forceDelete.value = checked || false,
                label: Resources.ForceDeleteReleaseDefinition,
                checked: this._forceDelete
            }));
    }

    private _forceDelete = new ObservableValue<boolean>(false);
}

const releasePipelineArtifactType: string = "Microsoft.TeamFoundation.ReleaseManagement.DefinitionEntry";
const artifactScopeType: string = "Project";

const newPipelineFeature = "Command_NewRD";
const importPipelineFeature = "Command_ImportRD";
const editRdFeature: string = "Command_EditRD";
const cloneRdFeature: string = "Command_CloneRD";
const deleteDefinitionFeature: string = "Command_DeleteDefinition";
const addToFavoritesFeature: string = "Command_AddToFavorites";
const removeFromFavoritesFeature: string = "Command_RemoveFromFavorites";
const exportRdFeature: string = "Command_ExportRD";

function isFavorite(pageContext: IVssPageContext, pipelineId: number | undefined | null): Promise<boolean> {
    if (!pipelineId) {
        return Promise.resolve(false);
    }

    const favoritesService: IFavoritesService = pageContext.getService("IFavoritesService");
    const project = _getProject(pageContext);
    return favoritesService.getFavorites(
        releasePipelineArtifactType,
        artifactScopeType,
        (project && project.id) || ""
    ).then((favorites: IFavoritesCollection) => {
        return (favorites && favorites.value.some((fav: Favorite) => parseInt(fav.artifactId) === pipelineId)) || false;
    });
}

function _getProject(pageContext: IVssPageContext): IProjectData | undefined {
    const pageData = getTFSPageData(pageContext);

    if (pageData) {
        return pageData.project;
    }
}


function saveToFile(str: string, fileName: string): void {
    const file = new Blob([str], { type: "application/json" });

    //needed for IE10
    if (window.navigator && (window.navigator as any).msSaveBlob) {
        (window.navigator as any).msSaveBlob(file, fileName);
    }
    else {
        // refer to the link http://stackoverflow.com/questions/31214677/download-a-reactjs-object-as-a-file for details
        // There is a package react-file-download (https://www.npmjs.com/package/react-file-download), but we do not have this 
        // downloaded in our repo.
        //
        // So we are using this approach as has been answered in the stack overflow thread mentioned above
        const a = document.createElement("a");
        document.body.appendChild(a);
        a.href = URL.createObjectURL(file);
        a.setAttribute("download", fileName);
        a.click();
    }
}

function _renderWrappedComponent<T extends IPipelineDialogPropsBase>(pageContext: IVssPageContext, componentType: string, props: T, children?: React.ReactNode[]): void {

    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
    const onClose = props.onClose;

    layoutManager.renderCallout((onDismiss: () => void) => {
        props.onClose = () => {
            if (onClose) {
                onClose();
            }
            onDismiss();
        };

        const extendedProps: IWrappedComponentProperties<T> = {
            ...(props as any),
            dependencies: ["ms.vss-releaseManagement-web.release-command-dialogs"],
            wrappedType: componentType,
        };
        return React.createElement(WrappedComponent, extendedProps, ...(children || []));
    });
}

function _getPivotBarAction(
    context: IReleaseDefinitionPivotCommandContext,
    key: string,
    name: string,
    iconProps: IIconProps,
    onClickFunctionName?: string,
    important?: boolean,
    children?: IMenuItem[],
    href?: string): IMenuItem {
    const command: IMenuItem = {
        id: key,
        text: name,
        important: important || false,
        iconProps: iconProps,
        href: href
    } as IMenuItem;

    if (children) {
        command.subMenuProps = {
            id: key + "-child-items",
            items: children
        };
    }

    if (onClickFunctionName) {
        command.onActivate = (menutItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            (context.pageContext.getService<IPipelineCommandsService>(pipelineCommandServiceName) as any)[onClickFunctionName](context, event);
        };
    }

    return command;
}

function _getEditPipelineUrl(pageContext: IVssPageContext, context: IReleaseDefinitionPivotCommandContext): string | undefined {
    if (context.pipelineId) {
        const queryParams: { [key: string]: string } = {
            "definitionId": context.pipelineId.toString(10),
            "_a": "environments-editor-preview"
        };

        return createUrlForRoute(pageContext, "ms.vss-releaseManagement-web.cd-workflow-hub-default-route", queryParams);
    }
}

function _getCreatePipelineUrl(pageContext: IVssPageContext, context: IReleaseDefinitionPivotCommandContext): string {
    const queryParams: { [key: string]: string } = {
        "definitionId": "0",
        "_a": "action-create-definition",
        "source": "ReleaseNew",
        "path": context.folderPath || "\\"
    };

    return createUrlForRoute(pageContext, "ms.vss-releaseManagement-web.cd-workflow-hub-default-route", queryParams);
}
function _getClonePipelineUrl(pageContext: IVssPageContext, context: IReleaseDefinitionPivotCommandContext): string | undefined {
    if (context.pipelineId) {
        const queryParams: { [key: string]: string } = {
            "sourceId": context.pipelineId.toString(10),
            "_a": "action-clone-definition",
            "source": "ReleaseClone"
        };

        return createUrlForRoute(pageContext, "ms.vss-releaseManagement-web.cd-workflow-hub-default-route", queryParams);
    }
}

const pipelineCommandServiceName = "IPipelineCommandsService"

Services.add(pipelineCommandServiceName, { serviceFactory: PipelineCommandsService });
